// Administration surface (admin mockup v1/v2): the desktop shell for sessions with the
// administrator role — navigation on the left, one view at a time, light by default.
// Views are routed by the URL hash so a page reload lands on the same view.

import { useEffect, useState } from "react";
import { Admin, adoptSession, applyPrefs, Auth, getSession, saveSession, type Overview } from "../api/client.js";
import { t } from "../i18n.js";
import { Brand } from "../ui/components.jsx";
import "./admin.css";
import { Deliveries } from "./Deliveries.jsx";
import { Outbound } from "./Outbound.jsx";
import { Counting } from "./Counting.jsx";
import { Automations } from "./Automations.jsx";
import { Layout } from "./Layout.jsx";
import { Deviations } from "./Deviations.jsx";
import { DevicesUsers } from "./DevicesUsers.jsx";
import { ErpReports } from "./ErpReports.jsx";
import { Events } from "./Events.jsx";
import { GtinRegisterView } from "./GtinRegister.jsx";
import { Locations } from "./Locations.jsx";
import { Printers } from "./Printers.jsx";
import { Templates } from "./Templates.jsx";
import { Sites, selectSite, selectedSiteId } from "./Sites.jsx";
import type { SiteOut } from "../api/client.js";
import { Products } from "./Products.jsx";
import { Profiles } from "./Profiles.jsx";
import { Agents } from "./Agents.jsx";
import { Profile } from "./Profile.jsx";
import { Stock } from "./Stock.jsx";
import { Suppliers } from "./Suppliers.jsx";
import { OverviewView } from "./Overview.jsx";
import { SiteSettings } from "./SiteSettings.jsx";

export type AdminView = "overview" | "deviations" | "deliveries" | "outbound" | "counting" | "events" | "erp-reports" | "stock" | "products" | "profiles" | "locations" | "layout" | "gtins" | "suppliers" | "devices" | "agents" | "printers" | "templates" | "sites" | "automations" | "settings" | "me";

function viewFromHash(): AdminView {
  const h = window.location.hash.replace(/^#\/?/, "").split("/")[0] ?? "";
  return (["deviations", "deliveries", "outbound", "counting", "events", "erp-reports", "stock", "products", "profiles", "locations", "layout", "gtins", "suppliers", "devices", "agents", "printers", "templates", "sites", "automations", "settings", "me"] as const).find((v) => v === h) ?? "overview";
}

export function AdminShell({ onTaskClient }: { onTaskClient: () => void }) {
  const [view, setView] = useState<AdminView>(viewFromHash);
  const [overview, setOverview] = useState<Overview | null>(null);
  const session = getSession();

  useEffect(() => {
    applyPrefs(undefined, "admin");
    const onHash = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      applyPrefs(undefined, "task");
    };
  }, []);

  const [erpFailed, setErpFailed] = useState(0);
  useEffect(() => {
    Admin.overview(selectedSiteId() ?? "all").then(setOverview).catch(() => setOverview(null));
    Admin.erpReportSummary().then((s) => setErpFailed(s.counts.failed)).catch(() => undefined);
  }, [view]);

  const go = (v: AdminView) => {
    window.location.hash = `/${v}`;
    setView(v);
  };
  // One person, several companies (owner 2026-10-29): a new session in the chosen tenant,
  // then the page reloads where it was — everything on it is that tenant's.
  const [sites, setSites] = useState<SiteOut[]>([]);
  const [siteSel, setSiteSel] = useState<string | null>(selectedSiteId());
  useEffect(() => {
    Admin.sites(false).then(setSites).catch(() => setSites([]));
    const on = () => { setSiteSel(selectedSiteId()); Admin.sites(false).then(setSites).catch(() => undefined); Admin.overview(selectedSiteId() ?? "all").then(setOverview).catch(() => undefined); };
    window.addEventListener("trail:site", on);
    return () => window.removeEventListener("trail:site", on);
  }, [view]);
  const switchTo = async (tenantId: string) => {
    if (!session || tenantId === session.tenantId) return;
    try {
      const s = await Auth.switchTenant(tenantId);
      await adoptSession(s.token, s.expiresAt);
      window.location.reload();
    } catch {
      /* the selector snaps back on the next render */
    }
  };
  const logout = async () => {
    try {
      await Auth.logout();
    } catch {
      /* dropped locally regardless */
    }
    saveSession(null);
    onTaskClient();
  };

  // Idle lock (company setting security.idleMinutes; docs/security.md): the desktop session is signed out after that long without input.
  useEffect(() => {
    let minutes = 0;
    Admin.tenantConfig().then((c) => { minutes = c.config.security?.idleMinutes ?? 0; }).catch(() => undefined);
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    for (const e of evs) window.addEventListener(e, bump, { passive: true });
    const timer = window.setInterval(() => { if (minutes > 0 && Date.now() - last > minutes * 60_000) void logout(); }, 30_000);
    return () => { for (const e of evs) window.removeEventListener(e, bump); window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const deviations = overview?.deviations.last30Days ?? null;
  const waiting = overview?.putAway.waiting ?? null;
  const receipts = overview?.receipts.length ?? null;

  return (
    <div className="adm">
      <nav className="adm-nav">
        <Brand />
        <div className="site">{session?.email ?? ""}</div>
        {sites.filter((s) => s.active).length > 1 && (
          <label className="tenant-sel" style={{ display: "block", margin: "0 8px 8px", fontSize: 12 }}>
            <span className="mut" style={{ display: "block", fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase" }}>{t("adm.nav.site")}</span>
            <select value={siteSel ?? sites.find((s) => s.active && s.isOwn)?.id ?? ""} onChange={(e) => { selectSite(e.target.value || null); setSiteSel(e.target.value || null); window.location.reload(); }}>
              {sites.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        {session && (session.tenants?.length ?? 0) > 1 && (
          <label className="tenant"><span>{t("login.tenant")}</span>
            <select value={session.tenantId} onChange={(e) => void switchTo(e.target.value)}>
              {session.tenants!.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <div className="grp">{t("adm.ops")}</div>
        <button type="button" className={`navlink${view === "overview" ? " on" : ""}`} onClick={() => go("overview")}>{t("adm.overview")}</button>
        <button type="button" className={`navlink${view === "deviations" ? " on" : ""}`} onClick={() => go("deviations")}>{t("adm.deviations")} <span className={`n${deviations ? " warn" : ""}`}>{deviations ?? ""}</span></button>
        <button type="button" className={`navlink${view === "deliveries" ? " on" : ""}`} onClick={() => go("deliveries")}>{t("adm.deliveries")} <span className="n">{receipts ?? ""}</span></button>
        <button type="button" className={`navlink${view === "outbound" ? " on" : ""}`} onClick={() => go("outbound")}>{t("adm.outbound")}</button>
        <button type="button" className={`navlink${view === "counting" ? " on" : ""}`} onClick={() => go("counting")}>{t("adm.counting")}</button>
        <button type="button" className={`navlink${view === "stock" ? " on" : ""}`} onClick={() => go("stock")}>{t("adm.stock")} <span className="n">{waiting ? `${waiting} ${t("adm.stock.waiting")}` : ""}</span></button>
        <button type="button" className={`navlink${view === "events" ? " on" : ""}`} onClick={() => go("events")}>{t("adm.events")}</button>
        <button type="button" className={`navlink${view === "erp-reports" ? " on" : ""}`} onClick={() => go("erp-reports")}>{t("adm.erpReports")} <span className={`n${erpFailed ? " warn" : ""}`}>{erpFailed || ""}</span></button>
        <div className="grp">{t("adm.registry")}</div>
        <button type="button" className={`navlink${view === "products" ? " on" : ""}`} onClick={() => go("products")}>{t("adm.products")}</button>
        <button type="button" className={`navlink${view === "profiles" ? " on" : ""}`} onClick={() => go("profiles")}>{t("adm.set.profiles")}</button>
        <button type="button" className={`navlink${view === "locations" ? " on" : ""}`} onClick={() => go("locations")}>{t("adm.locations")}</button>
        <button type="button" className={`navlink${view === "layout" ? " on" : ""}`} onClick={() => go("layout")}>{t("adm.layout")}</button>
        <button type="button" className={`navlink${view === "gtins" ? " on" : ""}`} onClick={() => go("gtins")}>{t("adm.gtins")}</button>
        <button type="button" className={`navlink${view === "suppliers" ? " on" : ""}`} onClick={() => go("suppliers")}>{t("adm.suppliers")}</button>
        <div className="grp">{t("adm.system")}</div>
        <button type="button" className={`navlink${view === "devices" ? " on" : ""}`} onClick={() => go("devices")}>{t("adm.devicesUsers")}</button>
        <button type="button" className={`navlink${view === "agents" ? " on" : ""}`} onClick={() => go("agents")}>{t("adm.agents")}</button>
        <button type="button" className={`navlink${view === "printers" ? " on" : ""}`} onClick={() => go("printers")}>{t("adm.printers")}</button>
        <button type="button" className={`navlink${view === "templates" ? " on" : ""}`} onClick={() => go("templates")}>{t("adm.templates")}</button>
        <button type="button" className={`navlink${view === "sites" ? " on" : ""}`} onClick={() => go("sites")}>{t("adm.sites")}</button>
        <button type="button" className={`navlink${view === "automations" ? " on" : ""}`} onClick={() => go("automations")}>{t("adm.automations")}</button>
        <button type="button" className={`navlink${view === "settings" ? " on" : ""}`} onClick={() => go("settings")}>{t("menu.settings")}</button>
        <div className="foot">
          <button type="button" className={`navlink${view === "me" ? " on" : ""}`} style={{ padding: "6px 8px" }} onClick={() => go("me")}>{session?.name} · {t("adm.me.title")}</button>
          <span>Release {__BUILD_VERSION__}</span>
          <button type="button" className="abtn sm" onClick={onTaskClient}>{t("adm.toTaskClient")}</button>
          <button type="button" className="abtn sm" onClick={() => void logout()}>{t("adm.logout")}</button>
        </div>
      </nav>
      <div className="adm-main">
        {view === "overview" && <OverviewView overview={overview} onGo={go} />}
        {view === "deviations" && <Deviations />}
        {view === "deliveries" && <Deliveries />}
        {view === "outbound" && <Outbound />}
        {view === "counting" && <Counting />}
        {view === "events" && <Events />}
        {view === "erp-reports" && <ErpReports />}
        {view === "stock" && <Stock />}
        {view === "products" && <Products />}
        {view === "locations" && <Locations />}
        {view === "layout" && <Layout />}
        {view === "gtins" && <GtinRegisterView />}
        {view === "suppliers" && <Suppliers />}
        {view === "me" && <Profile />}
        {view === "printers" && <Printers />}
        {view === "templates" && <Templates />}
        {view === "sites" && <Sites />}
        {view === "automations" && <Automations />}
        {view === "devices" && <DevicesUsers />}
        {view === "settings" && <SiteSettings />}
        {view === "profiles" && <Profiles />}
        {view === "agents" && <Agents />}
      </div>
    </div>
  );
}
